import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isExpired } from 'react-jwt';
import { format } from 'date-fns';
import {
  Card, Grid, Alert, Snackbar, TextField, MenuItem,
  Button, Autocomplete, Box
} from '@mui/material';
import MDBox from '../../../shared/MDBox';
import MDTypography from '../../../shared/MDTypography';
import { ReAuth } from '../../../authentication/reAuth';
import { verifyRolesAcess } from '../../../../services/Utils/verifyRolesAcess';
import * as ContratacaoV2Service from '../../ContratacaoV2Service';
import { IContratacaoV2Post } from '../../ContratacaoV2Type';
import PlanoService from '../../../../services/PlanoService';
import clienteService from '../../../../services/ClienteService';

const TIPOS_CONTRATO = ['Plano Regular', 'Psicomotricidade'];

interface ClienteOption {
  id: string;
  nomeCliente: string;
  cpf: string;
}

interface PlanoOption {
  id: string;
  descricao: string;
  valor: number;
}

function CadContratacaoV2Form() {
  const navigate = useNavigate();

  const [clienteId, setClienteId] = useState('');
  const [planoId, setPlanoId] = useState('');
  const [planoNome, setPlanoNome] = useState('');
  const [valorContratacao, setValorContratacao] = useState('');
  const [desconto, setDesconto] = useState('');
  const [observacao, setObservacao] = useState('');
  const [duracaoContrato, setDuracaoContrato] = useState('');
  const [tipoContrato, setTipoContrato] = useState('');
  const [diasSemanaAula, setDiasSemanaAula] = useState<string[]>([]);
  const [horarioAula, setHorarioAula] = useState('');
  const [dataInicioContratacao, setDataInicioContratacao] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [planos, setPlanos] = useState<PlanoOption[]>([]);

  const [tokenExpired, setTokenExpired] = useState(false);
  const [invalidPermission, setInvalidPermission] = useState(false);
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState('');
  const [msgError, setMsgError] = useState('');
  const [loading, setLoading] = useState(false);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const valorNum = parseFloat(valorContratacao.replace(',', '.')) || 0;
  const descontoNum = parseFloat(desconto.replace(',', '.')) || 0;
  const duracaoNum = parseInt(duracaoContrato) || 0;
  const valorTotal = (valorNum * duracaoNum) - descontoNum;

  useEffect(() => {
    async function init() {
      const perm = await verifyRolesAcess('funcionarios');
      setInvalidPermission(!perm);
      setTokenExpired(isExpired(localStorage.getItem('token')));

      try {
        const clientesRes = await clienteService.getAll();
        const data = clientesRes.data || [];
        setClientes(data.map((c: any) => ({
          id: c.id,
          nomeCliente: c.nomeCliente || c.nome || '',
          cpf: c.cpf || '',
        })));
      } catch (e) { console.error('Erro ao carregar clientes:', e); }

      try {
        const planosRes = await PlanoService.getAll();
        const data = planosRes.data || [];
        setPlanos(data.map((p: any) => ({
          id: p.id,
          descricao: p.descricao,
          valor: p.valor,
        })));
      } catch (e) { console.error('Erro ao carregar planos:', e); }
    }
    init();
  }, []);

  const handleReAuthInfo = () => {
    setTokenExpired(isExpired(localStorage.getItem('token')));
  };

  const validation = (): boolean => {
    if (!clienteId) { setAlertError(true); setMsgError('Selecione o cliente.'); return false; }
    if (!planoId) { setAlertError(true); setMsgError('Selecione o plano.'); return false; }
    if (valorNum <= 0) { setAlertError(true); setMsgError('Informe um valor válido.'); return false; }
    if (descontoNum < 0) { setAlertError(true); setMsgError('Desconto não pode ser negativo.'); return false; }
    if (descontoNum > valorNum) { setAlertError(true); setMsgError('Desconto não pode ser maior que o valor.'); return false; }
    if (!duracaoContrato || parseInt(duracaoContrato) <= 0) { setAlertError(true); setMsgError('Informe a duração do contrato.'); return false; }
    if (!tipoContrato) { setAlertError(true); setMsgError('Selecione o tipo de contrato.'); return false; }
    if (!dataInicioContratacao) { setAlertError(true); setMsgError('Informe a data da contratação.'); return false; }
    if (tipoContrato === 'Plano Regular') {
      if (diasSemanaAula.length === 0) { setAlertError(true); setMsgError('Selecione os dias de aula.'); return false; }
      if (!horarioAula || horarioAula.trim() === '') { setAlertError(true); setMsgError('Informe o horário de aula.'); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validation()) return;
    setLoading(true);

    try {
      const obj: IContratacaoV2Post = {
        clienteId,
        planoId,
        valorContratacao: valorNum,
        desconto: descontoNum,
        observacao: observacao || null,
        duracaoContrato: parseInt(duracaoContrato),
        tipoContrato,
        diasSemanaAula: tipoContrato === 'Plano Regular' ? diasSemanaAula.join(', ') : null,
        horarioAula: tipoContrato === 'Plano Regular' ? horarioAula || null : null,
        dataInicioContratacao,
        usuarioCriacao: null,
      };

      const result = await ContratacaoV2Service.cadastrarV2(obj);

      if (result.sucesso) {
        setAlertSuccess(true);
        setMsgSuccess(result.mensagemRetorno || 'Contratação cadastrada com sucesso!');

        if (result.id) {
          setTimeout(() => {
            handleDownloadContrato(result.id);
          }, 1000);
        }

        setTimeout(() => navigate('/contratacoes'), 2000);
      } else {
        setAlertError(true);
        setMsgError(result.mensagemRetorno || 'Erro ao cadastrar contratação.');
      }
    } catch (error: any) {
      setAlertError(true);
      setMsgError(error.response?.data?.mensagemRetorno || 'Erro ao cadastrar contratação.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadContrato = async (id: string) => {
    try {
      const blob = await ContratacaoV2Service.obterContratoPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrato_${planoNome || 'Plano'}_${tipoContrato || 'Contrato'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) { console.error('Erro ao baixar contrato:', e); }
  };

  const handleCloseSnak = () => {
    setTimeout(() => {
      setAlertError(false);
      setAlertSuccess(false);
      setMsgError('');
      setMsgSuccess('');
    }, 1200);
  };

  return (
    <>
      {tokenExpired ? (
        <ReAuth handleAuth={handleReAuthInfo} />
      ) : (
        <>
          {invalidPermission ? (
            <Alert severity="error">Você não tem permissão para acessar esse recurso.</Alert>
          ) : (
            <MDBox pt={6} pb={3}>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                      <MDTypography variant="h6" color="white">
                        Nova Contratação
                      </MDTypography>
                    </MDBox>

                    <Grid container spacing={3} p={4}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Data da Contratação"
                          type="date"
                          value={dataInicioContratacao}
                          onChange={(e) => setDataInicioContratacao(e.target.value)}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiInputBase-root': { height: 56 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={clientes}
                          getOptionLabel={(option: any) => option.nomeCliente || option.nome || ''}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(_, value: any) => {
                            setClienteId(value?.id || '');
                          }}
                          renderInput={(params) => <TextField {...params} label="Cliente" required />}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={planos}
                          getOptionLabel={(option: any) => option.descricao || ''}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(_, value: any) => {
                            setPlanoId(value?.id || '');
                            setPlanoNome(value?.descricao || '');
                            if (value?.valor) {
                              setValorContratacao(String(value.valor).replace('.', ','));
                            }
                          }}
                          renderInput={(params) => <TextField {...params} label="Plano" required />}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Tipo de Contrato"
                          value={tipoContrato}
                          onChange={(e) => {
                            setTipoContrato(e.target.value);
                            if (e.target.value !== 'Plano Regular') {
                              setDiasSemanaAula([]);
                              setHorarioAula('');
                            }
                          }}
                          select fullWidth required
                          sx={{ '& .MuiInputBase-root': { height: 56 } }}
                        >
                          {TIPOS_CONTRATO.map(t => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {tipoContrato === 'Plano Regular' && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).join(', ') }}
                              label="Dias de Aula"
                              value={diasSemanaAula}
                              onChange={(e) => setDiasSemanaAula(e.target.value as unknown as string[])}
                              fullWidth
                              required
                              sx={{ '& .MuiInputBase-root': { height: 56 } }}
                            >
                              <MenuItem value="Segunda">Segunda</MenuItem>
                              <MenuItem value="Terça">Terça</MenuItem>
                              <MenuItem value="Quarta">Quarta</MenuItem>
                              <MenuItem value="Quinta">Quinta</MenuItem>
                              <MenuItem value="Sexta">Sexta</MenuItem>
                              <MenuItem value="Sábado">Sábado</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Horário de Aula"
                              placeholder="09:00 - 10:00"
                              value={horarioAula}
                              onChange={(e) => setHorarioAula(e.target.value)}
                              fullWidth
                              required
                              sx={{ '& .MuiInputBase-root': { height: 56 } }}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Duração (meses)"
                          value={duracaoContrato}
                          onChange={(e) => setDuracaoContrato(e.target.value.replace(/[^0-9]/g, ''))}
                          fullWidth required type="text"
                          sx={{ '& .MuiInputBase-root': { height: 56 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Valor da Contratação (R$) / Mês"
                          value={valorContratacao}
                          onChange={(e) => setValorContratacao(e.target.value.replace(/[^0-9,]/g, ''))}
                          fullWidth required type="text"
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Desconto (R$)"
                          value={desconto}
                          onChange={(e) => setDesconto(e.target.value.replace(/[^0-9,]/g, ''))}
                          fullWidth type="text"
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f5f5f5', height: 56, display: 'flex', alignItems: 'center' }}>
                          <MDTypography variant="h6" sx={{ color: valorTotal >= 0 ? '#2e7d32' : '#d32f2f' }}>
                            Valor Total: R$ {formatCurrency(valorTotal)}
                          </MDTypography>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Observação"
                          value={observacao}
                          onChange={(e) => setObservacao(e.target.value)}
                          fullWidth multiline rows={3}
                        />
                      </Grid>

                      <Grid item xs={12} pt={2}>
                        <Button
                          variant="contained" fullWidth onClick={handleSubmit} disabled={loading}
                          style={{ backgroundColor: '#1976d2', color: '#fff' }}
                        >
                          {loading ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </Grid>

                      {alertError && (
                        <Grid item xs={12}>
                          <Alert severity="error" action={
                            <Button color="inherit" size="small" onClick={() => { setAlertError(false); setMsgError(''); }}>OK</Button>
                          }>
                            {msgError}
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          )}
        </>
      )}

      {alertSuccess && (
        <Snackbar open autoHideDuration={1500} onClose={handleCloseSnak}>
          <Alert onClose={handleCloseSnak} severity="success">{msgSuccess}</Alert>
        </Snackbar>
      )}
    </>
  );
}

export default CadContratacaoV2Form;
